import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';

import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
} from '@web/shared/ui';
import { AdminShippingMethodCollectionItem } from '../../models/admin-shipping-method-collection-item';
import { AdminShippingMethodCollectionFilter } from '../../models/admin-shipping-method-collection-filter';
import { AdminShippingMethodListItemAction } from '../../enums/admin-shipping-method-list-item-action';
import { AdminShippingMethodListCollectionAction } from '../../enums/admin-shipping-method-list-collection-action';
import { AdminShippingMethodDataSourceService } from '../../services/admin-shipping-method-data-source.service';
import { Observable } from 'rxjs';
import { Route, RouteParam } from '@web/common';
import { AdminShippingMethodListCustomCellTemplateComponent } from '../../components/admin-shipping-method-list-custom-cell-template/admin-shipping-method-list-custom-cell-template.component';
import { AdminDictionaryCountrySelectItemsProviderService } from '@web/shared/module/dictionary';

@Component({
  selector: 'app-admin-shipping-method-list',
  imports: [TableComponent, AdminShippingMethodListCustomCellTemplateComponent],
  templateUrl: './admin-shipping-method-list.component.html',
  styleUrl: './admin-shipping-method-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminShippingMethodListComponent implements OnInit {
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminShippingMethodCollectionItem, AdminShippingMethodCollectionFilter, AdminShippingMethodListItemAction, AdminShippingMethodListCollectionAction>).create();
  /* prettier-ignore */
  private readonly shippingMethodDataSource = inject(AdminShippingMethodDataSourceService);
  /* prettier-ignore */
  private readonly dictionaryCountrySelectItemsProvider = inject(AdminDictionaryCountrySelectItemsProviderService);

  protected config!: TableConfig<
    AdminShippingMethodCollectionItem,
    AdminShippingMethodCollectionFilter,
    AdminShippingMethodListItemAction,
    AdminShippingMethodListCollectionAction
  >;

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .itemActions((builder) => [
        builder
          .initRouterLink(AdminShippingMethodListItemAction.Preview)
          .visibility((item) => item.canBeViewed)
          .icon(Icon.Eye)
          .tooltip('Podgląd')
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.ShippingMethodDetails, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),

        builder
          .initRouterLink(AdminShippingMethodListItemAction.Edit)
          .visibility((item) => item.canBeEdited)
          .icon(Icon.Pencil)
          .tooltip('Edycja')
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.ShippingMethodEdit, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),
      ])
      .filters((builder) => [
        builder //
          .initText('description')
          .build(),

        builder //
          .initText('name')
          .build(),

        builder //
          .initMultiSelect('countries')
          .dictionary(() =>
            this.dictionaryCountrySelectItemsProvider.fetchSelectItems('label'),
          )
          .build(),

        builder //
          .initDateRange('createdAt')
          .build(),
      ])
      .columns((builder) => [
        builder //
          .initText('name')
          .label('Nazwa')
          .sortable(true)
          .sortKey('name')
          .filterable(true)
          .filterKey('name')
          .build(),

        builder //
          .initCustom('description')
          .width('40rem')
          .label('Opis')
          .sortable(true)
          .sortKey('description')
          .filterable(true)
          .filterKey('description')
          .build(),

        builder //
          .initCustom('countries')
          .width('40rem')
          .label('Kraje')
          .sortable(false)
          .filterable(true)
          .filterKey('countries')
          .build(),

        builder //
          .initDateTime('createdAt')
          .label('Data utworzenia')
          .sortable(true)
          .sortKey('createdAt')
          .filterable(true)
          .filterKey('createdAt')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<AdminShippingMethodCollectionFilter>,
        ): Observable<TableDataPayload<AdminShippingMethodCollectionItem>> =>
          this.shippingMethodDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }
}
